import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, ArrowUpRight, X } from "lucide-react";
import { useLocation, Link } from "react-router-dom";

const HIDE = ["/admin", "/login"];

/** Persistent slim CTA bar that fades in after scroll — friction-free access to primary action. */
export default function PersistentCTA() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const hidden = HIDE.some((r) => location.pathname.startsWith(r));

  useEffect(() => {
    if (hidden) return;
    function onScroll() {
      setVisible(window.scrollY > 800);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [hidden]);

  if (hidden) return null;

  return (
    <AnimatePresence>
      {visible && !dismissed && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="fixed z-40 bottom-6 left-1/2 -translate-x-1/2 max-w-[calc(100vw-2rem)]"
          data-testid="persistent-cta"
        >
          <div
            className="flex items-center gap-3 md:gap-5 px-4 md:px-6 py-3"
            style={{
              background: "rgba(11,11,13,0.85)",
              backdropFilter: "blur(24px) saturate(140%)",
              WebkitBackdropFilter: "blur(24px) saturate(140%)",
              border: "1px solid var(--line-gold)",
              borderRadius: "var(--r-full)",
              boxShadow: "0 24px 60px -12px rgba(0,0,0,0.85), 0 0 40px rgba(198,168,124,0.12)",
            }}
          >
            <div className="hidden md:flex items-center gap-3 pr-2">
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#4ade80" }} />
              <span className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--text-2)" }}>Available now</span>
            </div>
            <div className="hidden md:block w-px h-6" style={{ background: "var(--line)" }} />
            <span className="font-display text-lg md:text-xl" style={{ letterSpacing: "-0.015em", color: "var(--text)" }}>
              Ready when you are.
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <Link
                to="/pricing"
                data-testid="persistent-cta-pricing"
                data-cursor="hover"
                className="hidden sm:inline-flex items-center gap-2 px-4 md:px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.32em] transition-all duration-300"
                style={{
                  background: "var(--gold)",
                  color: "#0b0b0d",
                  borderRadius: "var(--r-full)",
                  border: "1px solid var(--gold)",
                }}
              >
                Instant Plan <ArrowUpRight size={12} />
              </Link>
              <a
                href="tel:+18338237765"
                data-testid="persistent-cta-call"
                className="inline-flex items-center gap-2 px-3 md:px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.32em] transition-all duration-300"
                style={{
                  border: "1px solid var(--line-2)",
                  color: "var(--text)",
                  borderRadius: "var(--r-full)",
                }}
              >
                <Phone size={12} />
                <span className="hidden sm:inline">(833) 8BESPOKE</span>
              </a>
              <button
                onClick={() => setDismissed(true)}
                data-testid="persistent-cta-close"
                className="w-8 h-8 flex items-center justify-center transition-all duration-300"
                style={{ border: "1px solid var(--line)", color: "var(--text-3)", borderRadius: "var(--r-full)" }}
                aria-label="Dismiss"
              >
                <X size={12} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
